import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Clock, Home, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function ProtectedRoute({ children, requireAdmin = false, requiredTier = null }) {
  const { user, loading, isApproved, isPending, isAdmin, hasTier } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FEFAF0] flex items-center justify-center">
        <div className="text-[#8D6E63] text-sm">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (isPending) {
    return (
      <div className="min-h-screen bg-[#FEFAF0] flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
            <Clock className="h-8 w-8 text-amber-700" />
          </div>
          <h2 className="text-xl font-serif text-[#3E2723] mb-3">Account Pending Approval</h2>
          <p className="text-sm text-[#8D6E63] mb-6 leading-relaxed">
            Your account is awaiting approval from the administrator.
            You&apos;ll be able to access the curriculum once approved.
          </p>
          <Button
            variant="outline"
            className="border-amber-800 text-amber-900 hover:bg-amber-50"
            onClick={() => window.location.href = '/'}
          >
            <Home className="h-4 w-4 mr-2" />Back to Home
          </Button>
        </div>
      </div>
    );
  }

  if (user.status === 'rejected') {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Subscription tier check
  if (requiredTier && !hasTier(requiredTier)) {
    return <SubscriptionGate requiredTier={requiredTier} />;
  }

  return children;
}

function SubscriptionGate({ requiredTier }) {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#FEFAF0] flex items-center justify-center p-4">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
          <Lock className="h-8 w-8 text-amber-700" />
        </div>
        <h2 className="text-xl font-serif text-[#3E2723] mb-3">
          {requiredTier === 'pro' ? 'Pro' : 'Basic'} Subscription Required
        </h2>
        <p className="text-sm text-[#8D6E63] mb-6 leading-relaxed">
          {requiredTier === 'pro'
            ? 'This tool requires a Pro subscription ($25/mo). Upgrade to access all curriculum tools.'
            : 'A Basic subscription ($10/mo) is required to browse curriculum content.'}
        </p>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" className="border-amber-800 text-amber-900 hover:bg-amber-50" onClick={() => nav('/')} data-testid="gate-home-btn">
            <Home className="h-4 w-4 mr-2" />Home
          </Button>
          <Button className="bg-[#3E2723] hover:bg-[#2C1A14] text-amber-50" onClick={() => nav('/subscription')} data-testid="gate-subscribe-btn">
            View Plans
          </Button>
        </div>
      </div>
    </div>
  );
}
